import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { MuiScrollbarService } from '../scrollbar';
import { closeFullscreen, openFullscreen } from '../utility/browser/helpers';
import { MuiHeroContent } from './mui-hero-content/mui-hero-content.component';


@Injectable({
  providedIn: 'root'
})
export class MuiHeroAreaService {
  readonly scrolled$: Observable<boolean>;
  readonly scrollDirection$: Observable<string>;
  readonly scrollPosition$: Observable<number>;
  readonly heroContent$: Observable<MuiHeroContent | null>;
  readonly isPageFullScreen$: Observable<boolean> = of(false);
  readonly mobileWidth = 991;

  set scrolledThreshold(px: number) {
    this._scrolledThreshold.next(px);
  }

  private _scrolledThreshold = new BehaviorSubject<number>(16); // 1rem
  private _oldScrollPosition = 0;

  set heroContent(newContent: MuiHeroContent) {
    this._heroContent.next(newContent);
  }

  private _heroContent: BehaviorSubject<MuiHeroContent | null>;

  get isFullscreen(): boolean {
    return this._isFullscreen.getValue();
  }

  set isFullscreen(value: boolean) {
    value ? openFullscreen() : closeFullscreen();
    this._isFullscreen.next(value);
  }

  private _isFullscreen = new BehaviorSubject<boolean>(false);

  constructor(private scrollbarService: MuiScrollbarService) {
    this.scrollPosition$ = this.scrollbarService.scrollPosition;

    this.scrollDirection$ = this.scrollPosition$.pipe(
      map((position) => {
        const dir = position > this._oldScrollPosition ? 'down' : 'up';
        this._oldScrollPosition = position;
        return dir;
      })
    );

    this.scrolled$ = combineLatest([this.scrollPosition$, this._scrolledThreshold.asObservable(), this.scrollDirection$]).pipe(
      map(([pos, threshold, dir]) => {
        if (pos > threshold && window.innerWidth <= this.mobileWidth) {
          return dir === 'up';
        } else {
          return pos > threshold;
        }
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this._heroContent = new BehaviorSubject<MuiHeroContent | null>(null);
    this.heroContent$ = this._heroContent.asObservable();
    this.isPageFullScreen$ = this._isFullscreen.asObservable();

    /* Disable the fixed header in e2e tests because this causes some of the tests to fail. Long running animations caused the
     header to overlay the element to be clicked in tests. */
    if (navigator.webdriver) {
      this.scrolled$ = of(false);
    }
  }
}
